import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Pencil } from "lucide-react";
import { useOutlet } from "@/contexts/OutletContext";

const Outlets = () => {
  const { active, switchOutlet, reload } = useOutlet();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", spbu_code: "", address: "", active: true });

  const load = () => api.get("/outlets").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/outlets/${editing}`, form);
      else await api.post("/outlets", form);
      toast.success(editing ? "Outlet diperbarui" : "Outlet dibuat");
      setOpen(false); setEditing(null);
      setForm({ code: "", name: "", spbu_code: "", address: "", active: true });
      load(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const del = async (code) => {
    if (!window.confirm(`Hapus outlet ${code}? Data yang sudah terinput tidak akan hilang.`)) return;
    try { await api.delete(`/outlets/${code}`); toast.success("Terhapus"); load(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const startEdit = (o) => {
    setEditing(o.code);
    setForm({ code: o.code, name: o.name, spbu_code: o.spbu_code || "", address: o.address || "", active: !!o.active });
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Manajemen Outlet</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola cabang SPBU. Setiap outlet punya buku besar & neraca terpisah.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ code: "", name: "", spbu_code: "", address: "", active: true }); } }}>
          <DialogTrigger asChild><Button data-testid="add-outlet-button" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-2" />Tambah Outlet</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Outlet" : "Tambah Outlet Baru"}</DialogTitle>
              <DialogDescription>Kode outlet unik dan tidak dapat diubah setelah dibuat.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Kode Outlet</Label><Input value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} required disabled={!!editing} placeholder="jakarta-01" /></div>
              <div><Label>Nama Outlet</Label><Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="SPBU Jakarta Sudirman" /></div>
              <div><Label>Kode SPBU (Pertamina)</Label><Input value={form.spbu_code} onChange={(e) => setForm(f => ({ ...f, spbu_code: e.target.value }))} placeholder="34.12304" /></div>
              <div><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))} />
                <Label>Aktif</Label>
              </div>
              <DialogFooter><Button type="submit" className="bg-[#E30613] hover:bg-[#C8040F]">{editing ? "Simpan Perubahan" : "Buat Outlet"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold flex items-center gap-2"><Building2 className="w-5 h-5" />Daftar Outlet</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Kode SPBU</TableHead>
              <TableHead>Alamat</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.code}>
                  <TableCell className="font-mono-fig font-semibold">
                    {o.code}
                    {o.code === active && <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-200">Aktif</Badge>}
                  </TableCell>
                  <TableCell>{o.name}</TableCell>
                  <TableCell className="font-mono-fig text-sm">{o.spbu_code || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{o.address || "-"}</TableCell>
                  <TableCell>{o.active ? <Badge variant="secondary">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {o.code !== active && <Button size="sm" variant="outline" onClick={() => switchOutlet(o.code)}>Pindah ke sini</Button>}
                      <Button size="icon" variant="ghost" onClick={() => startEdit(o)}><Pencil className="w-4 h-4" /></Button>
                      {o.code !== "default" && <Button size="icon" variant="ghost" onClick={() => del(o.code)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Outlets;
